// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from '@/i18n/router';
import { useAuth } from '@/hooks/useAuth';
import { supabaseClient } from '@/lib/supabaseClient';

interface BlogNotification {
  id: string;
  post_id: string | null;
  type: 'new_comment' | 'new_content' | 'loyalty_tier_upgrade' | 'loyalty_points' | 'referral_bonus';
  message: string;
  post_title: string;
  post_slug: string | null;
  read: boolean;
  created_at: string;
}

export default function BlogNotificationsPanel() {
  useStoreLanguage();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<BlogNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const unread = notifications.filter(n => !n.read).length;

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fetchNotifications = useCallback(async () => {
    if (!supabaseClient || !user?.id) return;
    setLoading(true);
    try {
      const { data } = await supabaseClient
        .from('blog_favorite_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      setNotifications((data as BlogNotification[]) || []);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  // Polling cada 60s para nuevas notificaciones
  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, user?.id]);

  useEffect(() => {
    if (user?.id) fetchNotifications();
  }, [fetchNotifications, user?.id]);

  const markAllRead = async () => {
    if (!supabaseClient || !user?.id) return;
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return;
    await supabaseClient
      .from('blog_favorite_notifications')
      .update({ read: true })
      .in('id', unreadIds);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const getNotifIcon = (type: string) => {
    const map: Record<string, { icon: string; bg: string; color: string }> = {
      new_comment: { icon: 'ri-chat-1-line', bg: 'bg-teal-100', color: 'text-teal-600' },
      new_content: { icon: 'ri-article-line', bg: 'bg-amber-100', color: 'text-amber-600' },
      loyalty_tier_upgrade: { icon: 'ri-vip-crown-line', bg: 'bg-yellow-100', color: 'text-yellow-600' },
      loyalty_points: { icon: 'ri-star-line', bg: 'bg-green-100', color: 'text-green-600' },
      referral_bonus: { icon: 'ri-user-shared-line', bg: 'bg-teal-100', color: 'text-teal-600' },
    };
    return map[type] ?? { icon: 'ri-notification-3-line', bg: 'bg-gray-100', color: 'text-gray-500' };
  };

  const markRead = async (id: string) => {
    if (!supabaseClient) return;
    await supabaseClient
      .from('blog_favorite_notifications')
      .update({ read: true })
      .eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const deleteNotification = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!supabaseClient) return;
    await supabaseClient.from('blog_favorite_notifications').delete().eq('id', id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const formatTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `hace ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `hace ${days}d`;
  };

  if (!user) return null;

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => { setOpen(v => !v); if (!open && unread > 0) markAllRead(); }}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-forest-600 hover:bg-forest-50 transition-colors cursor-pointer"
        title={tr("Notificaciones del blog")}
      >
        <i className={`${unread > 0 ? 'ri-notification-3-fill text-forest-600' : 'ri-notification-3-line'} text-lg`}></i>
        {tr(unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full leading-none">
            {tr(unread > 9 ? '9+' : unread)}
          </span>
        ))}
      </button>

      {/* Panel */}
      {tr(open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-100 shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-notification-3-line text-forest-600"></i>
              <span className="text-sm font-semibold text-gray-900">{tr("Notificaciones")}</span>
              {tr(unread > 0 && (
                <span className="bg-red-100 text-red-600 text-xs font-bold px-1.5 py-0.5 rounded-full">{tr(unread)}</span>
              ))}
            </div>
            {tr(notifications.some(n => !n.read) && (
              <button
                onClick={markAllRead}
                className="text-xs text-forest-600 hover:text-forest-700 font-medium cursor-pointer whitespace-nowrap"
              >{tr("Marcar todo leído")}</button>
            ))}
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {tr(loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <i className="ri-loader-4-line animate-spin text-gray-400 text-xl"></i>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-10 px-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                  <i className="ri-notification-off-line text-xl text-gray-400"></i>
                </div>
                <p className="text-sm text-gray-500 mb-1">{tr("Sin notificaciones")}</p>
                <p className="text-xs text-gray-400">{tr("Te avisaremos cuando haya novedades en tus artículos favoritos")}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {tr(notifications.map(notif => {
                  const iconCfg = getNotifIcon(notif.type);
                  const isLoyalty = notif.type === 'loyalty_tier_upgrade' || notif.type === 'loyalty_points' || notif.type === 'referral_bonus';
                  const href = isLoyalty ? '/account' : `/blog/${notif.post_slug}`;
                  return (
                    <Link
                      key={notif.id}
                      to={href}
                      onClick={() => markRead(notif.id)}
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group ${!notif.read ? 'bg-forest-50/40' : ''}`}
                    >
                      {/* Icon */}
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${iconCfg.bg}`}>
                        <i className={`text-sm ${iconCfg.icon} ${iconCfg.color}`}></i>
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-800 line-clamp-1">{tr(notif.post_title)}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">{tr(notif.message)}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{tr(formatTime(notif.created_at))}</p>
                      </div>

                      {/* Unread dot + delete */}
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        {tr(!notif.read && (
                          <div className="w-2 h-2 bg-forest-500 rounded-full"></div>
                        ))}
                        <button
                          onClick={(e) => deleteNotification(notif.id, e)}
                          className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center text-gray-300 hover:text-red-400 transition-all cursor-pointer"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    </Link>
                  );
                }))}
              </div>
            ))}
          </div>

          {/* Footer */}
          {tr(notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-2">
              <Link
                to="/account"
                onClick={() => setOpen(false)}
                className="flex items-center gap-1.5 text-xs text-forest-600 hover:text-forest-700 font-medium cursor-pointer"
              >
                <i className="ri-bookmark-line"></i>{tr("Mis favoritos")}</Link>
              <Link
                to="/account"
                onClick={() => setOpen(false)}
                className="flex items-center gap-1.5 text-xs text-forest-600 hover:text-forest-700 font-medium cursor-pointer"
              >
                <i className="ri-star-line"></i>{tr("Mis puntos")}</Link>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
